import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_BASE_URL } from '@/constants';
import { toApiMeetingMode, toApiMeetingType } from '@/constants/meetings';
import { useAuthStore } from '@/store/authStore';
import { useSuperAdminAuthStore } from '@/store/superAdminAuthStore';
import type { ApiResponse, AuthTokens } from '@/types/api';

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

function rewriteMeetingPayload(data: unknown): unknown {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return data;
  const body = { ...(data as Record<string, unknown>) };
  if (typeof body.meetingType === 'string') {
    body.meetingType = body.meetingType === 'special' ? 'egm' : toApiMeetingType(body.meetingType) ?? body.meetingType;
  }
  if (typeof body.mode === 'string') {
    body.mode = body.mode === 'in_person' ? 'offline' : toApiMeetingMode(body.mode) ?? body.mode;
  }
  return body;
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken, tenant, user } = useAuthStore.getState();
  const { token: platformToken, impersonation } = useSuperAdminAuthStore.getState();

  const url = `${config.baseURL || ''}${config.url || ''}`;

  if (url.includes('/platform') && platformToken) {
    config.headers.Authorization = `Bearer ${platformToken}`;
  } else if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else if (platformToken) {
    config.headers.Authorization = `Bearer ${platformToken}`;
  }

  // Tenant ID resolution (support impersonation override)
  if (impersonation?.isImpersonating && impersonation.targetSocietyId) {
    config.headers['x-tenant-id'] = impersonation.targetSocietyId;
  } else {
    const tenantId = tenant?.id || tenant?._id || user?.tenantId;
    if (tenantId) {
      config.headers['x-tenant-id'] = tenantId;
    }
  }

  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch'].includes(method) && url.includes('/meetings')) {
    const raw = config.data;
    if (typeof raw === 'string') {
      try {
        config.data = rewriteMeetingPayload(JSON.parse(raw));
      } catch {
        /* keep original body */
      }
    } else {
      config.data = rewriteMeetingPayload(raw);
    }
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    if (status !== 401 || !original || original._retry) {
      return Promise.reject(error);
    }

    const url = `${original.baseURL || ''}${original.url || ''}`;

    // Super Admin / Platform endpoints handle 401 separately
    if (url.includes('/platform')) {
      useSuperAdminAuthStore.getState().clearPlatformAuth();
      return Promise.reject(error);
    }

    // Do not attempt refresh on auth endpoints (login, register, forgot-password)
    if (url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    const { refreshToken, logout, setTokens } = useAuthStore.getState();
    if (!refreshToken) {
      logout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token: string) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(original));
          },
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post<ApiResponse<AuthTokens>>(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );

      const tokens = data.data;
      setTokens(tokens.accessToken, tokens.refreshToken);
      processQueue(null, tokens.accessToken);
      original.headers.Authorization = `Bearer ${tokens.accessToken}`;
      return apiClient(original);
    } catch (refreshError) {
      processQueue(refreshError, null);
      logout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
